/**
 * ActiveContext — Aktiver Bearbeitungs-Kontext des Users (Joerg 2026-07-08).
 *
 * Liest `GET /api/portal/context/active` und exponiert das Ergebnis fuer
 * andere Module (Chat, Assistent, Notiz, Bibliothek, Topbar-Chip, ...).
 *
 * Reload-Trigger:
 *   1. Provider-Mount
 *   2. window `focus` (Rueckkehr aus anderem Tab)
 *   3. CustomEvent `active-context-refresh` (aus dem Zeiterfassungs-/BV2-
 *      Modul nach Timer-Start/-Stopp per `triggerActiveRefresh()`)
 *
 * Der Store hinter dem Context ist zusaetzlich per `getActiveSnapshot()`
 * ausserhalb von React auslesbar — genutzt vom `apiFetch`-Wrapper, um
 * `X-Active-*`-Header an Modul-Backends zu haengen.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { EMPTY_ACTIVE, api, getActiveSnapshot, setActiveSnapshot, subscribeActive, type ActiveSnapshot, useAuth } from '@onvice/portal-shell-api';

const ActiveReactContext = createContext<ActiveSnapshot>(EMPTY_ACTIVE);

interface ContextResponse {
  success: boolean;
  data: ActiveSnapshot | null;
}

async function loadActive(): Promise<void> {
  try {
    const r = await api.get<ContextResponse>('/portal/context/active');
    if (r.success) {
      setActiveSnapshot(r.data ?? EMPTY_ACTIVE);
    }
  } catch {
    // Kein Access / Netzwerk-Fehler — Kontext bleibt was er war.
  }
}

interface Props {
  children: ReactNode;
}

export function ActiveContextProvider({ children }: Props) {
  const { isAuthenticated } = useAuth();
  const [snap, setSnap] = useState<ActiveSnapshot>(() => getActiveSnapshot());

  useEffect(() => {
    // 2026-07-08 (Joerg): Pre-Login KEINEN Endpoint-Call machen. Sonst
    // 401 → apiFetch versucht Refresh → Refresh 401 → window.location='/login'
    // → Login-Screen mountet neu → Provider mountet neu → Loop → Flicker.
    if (!isAuthenticated) {
      setActiveSnapshot(EMPTY_ACTIVE);
      return;
    }
    void loadActive();
    const unsub = subscribeActive(setSnap);
    const onFocus = () => { void loadActive(); };
    const onRefresh = () => { void loadActive(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('active-context-refresh', onRefresh);
    return () => {
      unsub();
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('active-context-refresh', onRefresh);
    };
  }, [isAuthenticated]);

  return <ActiveReactContext.Provider value={snap}>{children}</ActiveReactContext.Provider>;
}

/**
 * Hook fuer Module: `const { timeEntry, task, customer } = useActive();`
 */
export function useActive(): ActiveSnapshot {
  return useContext(ActiveReactContext);
}
